import { useCallback, useEffect, useMemo, useState } from 'react';
import { distinctUntilChanged } from 'rxjs/operators';
import { Fighter } from '../../../../corelogic/domain/models/fighter.model';
import { buildFighterKey } from '../../../../corelogic/domain/utils/fighterImage.utils';
import {
  getFighterImage,
  getFighterImageIndividualLoading,
  getFighterImageLoading,
} from '../../../../corelogic/state/image/image.selector';
import { store } from '../../../../corelogic/state/store';

// Custom hook for single fighter image
export const useFighterImageState = (
  firstName: string | undefined,
  lastName: string | undefined,
  dob: Date | undefined,
) => {
  const [fighterImage, setFighterImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Create stable fighter key string for consistency with useAllFighterImagesState
  const fighterKeyString = useMemo(() => {
    const key = buildFighterKey(firstName, lastName, dob);
    return key || '';
  }, [firstName, lastName, dob]); // Simplified dependencies

  // Memoize the subscription setup to prevent recreating observables
  const subscribeToFighter = useCallback(() => {
    if (!fighterKeyString) {
      setFighterImage(null);
      setIsLoading(false);
      return;
    }

    // Pure reactive approach - let observables handle all state changes
    const imageSubscription = store
      .select(getFighterImage(fighterKeyString))
      .pipe(distinctUntilChanged())
      .subscribe((image) => {
        setFighterImage(image || null);
      });

    const loadingSubscription = store
      .select(getFighterImageIndividualLoading(fighterKeyString))
      .pipe(distinctUntilChanged())
      .subscribe(setIsLoading);

    return () => {
      imageSubscription.unsubscribe();
      loadingSubscription.unsubscribe();
    };
  }, [fighterKeyString]);

  useEffect(() => {
    return subscribeToFighter();
  }, [subscribeToFighter]);

  return { fighterImage, isLoading };
};

// Custom hook for multiple fighter images
export const useAllFighterImagesState = (fighters: (Fighter | undefined)[]) => {
  const [fighterImages, setFighterImages] = useState<
    Record<string, string | null>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {},
  );

  // Create stable list of fighter keys
  const fighterKeys = useMemo(() => {
    return fighters
      .filter(
        (fighter): fighter is Fighter =>
          Boolean(fighter?.firstname || fighter?.lastname) &&
          Boolean(fighter?.dob),
      )
      .map((fighter) =>
        buildFighterKey(fighter.firstname, fighter.lastname, fighter.dob),
      )
      .filter((key): key is string => key !== null)
      .sort(); // Sort for consistent ordering
  }, [fighters]);

  // Memoize subscription management based on fighter keys
  const subscribeToAllFighters = useCallback(() => {
    if (fighterKeys.length === 0) {
      setFighterImages({});
      setLoadingStates({});
      setIsLoading(false);
      return;
    }

    // Pure reactive approach - let observables handle all state changes
    const subscriptions = fighterKeys.flatMap((fighterKey) => [
      store
        .select(getFighterImage(fighterKey))
        .pipe(distinctUntilChanged())
        .subscribe((image) => {
          if (image) {
            setFighterImages((prev) => {
              // Only update if the image is actually different
              if (prev[fighterKey] === image) {
                return prev;
              }
              return { ...prev, [fighterKey]: image };
            });
          }
        }),

      store
        .select(getFighterImageIndividualLoading(fighterKey))
        .pipe(distinctUntilChanged())
        .subscribe((loading) => {
          setLoadingStates((prev) => {
            if (prev[fighterKey] === loading) return prev;
            return { ...prev, [fighterKey]: loading };
          });
        }),
    ]);

    // Subscribe to global loading state
    const globalLoadingSubscription = store
      .select(getFighterImageLoading)
      .pipe(distinctUntilChanged())
      .subscribe(setIsLoading);

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
      globalLoadingSubscription.unsubscribe();
    };
  }, [fighterKeys]);

  useEffect(() => {
    return subscribeToAllFighters();
  }, [subscribeToAllFighters]);

  return { fighterImages, isLoading, loadingStates };
};
